import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import { toast } from 'react-toastify';
// constants
import { REWARDS_STATUS_TYPE_BADGE } from '../../../../../../constants/consumers';
// API
import { useEnv } from '../../../../../../context/env-context';
import { useAuth } from '../../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../../utils/makeRequest';
// iconcs
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
// components
import ReactJsonEditor from '../../../../../../components/ReactJsonEditor';
// modals
import RewardAssignToModal from './modals/RewardAssignToModal';
import RewardSettingsModal from './modals/RewardSettingsModal';
import RewardUploadFileModal from './modals/RewardUploadFileModal';

function Reward({ reward, setReward, getRewards, setTabSpinner, aggregators }) {
  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const fileInputRef = useRef(null);
  const jsonEditorRef = useRef(null);

  const largeIconIntervalRef = useRef(null);
  const smallIconIntervalRef = useRef(null);

  const [imageLoaded, setImageLoaded] = useState({ small: false, large: false });
  const [imgCacheRefresh, setImgCacheRefresh] = useState({
    small: new Date().getTime(),
    large: new Date().getTime(),
  });
  const [updateIcon, setUpdateIcon] = useState({ small: false, large: false });

  const [language, setLanguage] = useState({ name: 'en', description: 'en' });

  const [uploadFileModal, setUploadFileModal] = useState(null);
  const [settingsModal, setSettingsModal] = useState(null);
  const [assignToModal, setAssignToModal] = useState(false);

  const saveReward = async () => {
    setTabSpinner(true);

    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    let data = reward;

    if (reward.category === 'VIDEO' || reward.category === 'ARTICLE') {
      data = { ...data, knowledgeCheck: jsonEditorRef.current.get() };
    }

    if (reward.category === 'SURVEY') {
      data = { ...data, survey: jsonEditorRef.current.get() };
    }

    const config = {
      token,
      url: `${apiOriginConsumer}/rewards/type/save`,
      method: 'POST',
      data: JSON.stringify(data),
    };

    try {
      await makeRequest(config);
      await getRewards();
      setReward(null);
      toast.success('Reward updated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setTabSpinner(false);
    }
  };

  const handleFileUpload = async e => {
    const file = e.target.files[0];

    fileInputRef.current.value = null;

    if (!file) return;

    if (file.type !== 'application/json') {
      toast.error('Only .json files are allowed!');
    }

    const text = await file.text();

    setReward(prev => ({
      ...prev,
      [reward.category === 'SURVEY' ? 'survey' : 'knowledgeCheck']: JSON.parse(text),
    }));
  };

  const importKNC = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const exportKNC = () => {
    const editorCurrentValue = jsonEditorRef.current.get();

    const url = window.URL.createObjectURL(new Blob([JSON.stringify(editorCurrentValue)]));
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute(
      'download',
      reward.category === 'SURVEY' ? 'survey.json' : 'knowledge_check.json',
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const refreshIcon = size => {
    let ref = size === 'large' ? largeIconIntervalRef.current : smallIconIntervalRef.current;

    if (ref) {
      clearInterval(ref);
    }

    ref = setInterval(() => {
      setImgCacheRefresh(prev => ({ ...prev, [size]: new Date().getTime() }));
    }, 5000);

    setTimeout(() => clearInterval(ref), 60000);

    setUpdateIcon(prev => ({ ...prev, [size]: false }));
  };

  useEffect(() => {
    if (updateIcon.large) {
      setImageLoaded(prev => ({ ...prev, large: false }));
      refreshIcon('large');
    }

    if (updateIcon.small) {
      setImageLoaded(prev => ({ ...prev, small: false }));
      refreshIcon('small');
    }
  }, [updateIcon]);

  useEffect(() => () => clearInterval(largeIconIntervalRef.current), []);

  const settingsArray = useMemo(() => {
    if (reward.category === 'VIDEO' || reward.category === 'ARTICLE') {
      return [
        ['Days To Complete', 'daysToComplete'],
        ['Number of Questions', 'numberOfQuestions'],
        ['Required Number of Correct Answers', 'requiredNumberOfCorrectAnswers'],
        ['Allowed Trials', 'allowedTrials'],
        ['Cooling Days Failed', 'coolingDaysFailed'],
        ['Cooling Days Succeeded', 'coolingDaysSucceeded'],
      ];
    }

    if (reward.category === 'SURVEY') {
      return [
        ['Days To Complete', 'daysToComplete'],
        ['Number of Questions', 'numberOfQuestions'],
        ['Cooling Days Failed', 'coolingDaysFailed'],
        ['Cooling Days Succeeded', 'coolingDaysSucceeded'],
      ];
    }

    if (reward.category === 'CHALLENGE') {
      return Object.keys(reward.data).map(x => [x, x]);
    }

    return [];
  }, [reward]);

  return (
    <>
      <div className="d-flex flex-column align-items-start">
        <Button className="bo-button-inline-link px-0 fw-bold mb-2" onClick={() => setReward(null)}>
          {'< Back to Reward Types List'}
        </Button>
        <div className="w-100 d-flex justify-content-between align-items-end gap-2 mb-3">
          <div>
            <Form.Label className="mb-1">ID</Form.Label>
            <Form.Control className="bo-input bo-w-400" type="text" value={reward.type} readOnly />
          </div>
          <Button className="bo-button bo-w-120" onClick={saveReward}>
            Save
          </Button>
        </div>
        <div className="w-100 d-flex gap-3">
          <div className="d-flex flex-column gap-3">
            <div>
              <Form.Label className="mb-1">Category</Form.Label>
              <Form.Control
                className="bo-input bo-w-400"
                type="text"
                value={reward.category}
                readOnly
              />
            </div>
            <div className="d-flex gap-3">
              <div>
                <Form.Label className="mb-1">Name</Form.Label>
                <Form.Control
                  className="bo-input bo-w-400"
                  type="text"
                  value={reward.name[language.name] || ''}
                  onChange={e =>
                    setReward(prev => ({
                      ...prev,
                      name: { ...prev.name, [language.name]: e.target.value },
                    }))
                  }
                />
              </div>
              <Form.Select
                className="bo-filter bo-max-w-120 align-self-end"
                value={language.name}
                onChange={e => setLanguage(prev => ({ ...prev, name: e.target.value }))}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </Form.Select>
            </div>
            <div className="d-flex gap-3">
              <div>
                <Form.Label className="mb-1">Description</Form.Label>
                <Form.Control
                  className="bo-input bo-w-400 bo-h-60-fixed bo-resize-none"
                  as="textarea"
                  value={reward.description[language.description] || ''}
                  onChange={e =>
                    setReward(prev => ({
                      ...prev,
                      description: { ...prev.description, [language.description]: e.target.value },
                    }))
                  }
                />
              </div>
              <Form.Select
                className="bo-filter bo-max-w-120 align-self-end"
                value={language.description}
                onChange={e => setLanguage(prev => ({ ...prev, description: e.target.value }))}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
              </Form.Select>
            </div>
            <div>
              <Form.Label className="mb-1">Bonus</Form.Label>
              <NumericFormat
                className="bo-input bo-w-100"
                value={reward.bonus || ''}
                onValueChange={({ floatValue }) =>
                  setReward(prev => ({ ...prev, bonus: floatValue }))
                }
                customInput={Form.Control}
                allowNegative={false}
                decimalScale={0}
              />
            </div>
            <div>
              <Form.Label className="mb-1">Status</Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  className={classNames(
                    'bo-dropdown-toggle-light',
                    `bg-${REWARDS_STATUS_TYPE_BADGE[reward.status]?.color}`,
                  )}
                >
                  <p className="p-0 m-0">{REWARDS_STATUS_TYPE_BADGE[reward.status]?.text}</p>
                </Dropdown.Toggle>
                <Dropdown.Menu className="bo-dropdown-menu-alt">
                  {Object.entries(REWARDS_STATUS_TYPE_BADGE).map(([key, value]) => (
                    <Dropdown.Item
                      key={key}
                      className={classNames(
                        'bo-dropdown-menu-alt-item bo-w-180 text-white fw-bold',
                        `bg-${value?.color}`,
                      )}
                      onClick={() => setReward(prev => ({ ...prev, status: key }))}
                    >
                      {value?.text}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div>
              <Form.Label className="mb-1">A/B Testing Group</Form.Label>
              <Form.Control
                className="bo-input bo-w-200"
                type="text"
                value={reward.abTestingGroup || ''}
                onChange={e => setReward(prev => ({ ...prev, abTestingGroup: e.target.value }))}
              />
            </div>
          </div>
          <div className="d-flex bo-w-500 justify-content-around gap-3 me-3">
            <div className="d-flex flex-column align-items-start">
              <Form.Label className="bo-text-sm mb-0">Small Icon</Form.Label>
              <Form.Label className="bo-text-xs mb-1">(128x128)</Form.Label>
              <div className="position-relative">
                {!imageLoaded.small && (
                  <div
                    className={classNames(
                      'position-absolute z-3 border',
                      'rounded p-1 bo-min-w-64 bo-min-h-64 bg-white',
                    )}
                  />
                )}
                <img
                  className="border rounded p-1 bo-min-w-64 bo-min-h-64 bo-max-w-64 bo-max-h-64"
                  src={
                    `https://bridgeover.app/resources/rewards/${reward.type}/icon_128.png` +
                    `?${imgCacheRefresh.small}`
                  }
                  onLoad={() => setImageLoaded(prev => ({ ...prev, small: true }))}
                  alt="small reward icon"
                />
              </div>
              <Button
                className="bo-common-inline-button text-nowrap mt-1"
                onClick={() => setUploadFileModal({ type: 'icon', accept: '.png', iconSize: 128 })}
              >
                Upload .png
              </Button>
            </div>
            <div className="d-flex flex-column align-items-start">
              <Form.Label className="bo-text-sm mb-0">Large Icon</Form.Label>
              <Form.Label className="bo-text-xs mb-1">(256x256)</Form.Label>
              <div className="position-relative">
                {!imageLoaded.large && (
                  <div
                    className={classNames(
                      'position-absolute z-3 border',
                      'rounded p-1 bo-min-w-128 bo-min-h-128 bg-white',
                    )}
                  />
                )}
                <img
                  className="border rounded p-1 bo-min-w-128 bo-min-h-128 bo-max-w-128 bo-max-h-128"
                  src={
                    `https://bridgeover.app/resources/rewards/${reward.type}/icon_256.png` +
                    `?${imgCacheRefresh.large}`
                  }
                  onLoad={() => setImageLoaded(prev => ({ ...prev, large: true }))}
                  alt="large reward icon"
                />
              </div>
              <Button
                className="bo-common-inline-button text-nowrap mt-1"
                onClick={() => setUploadFileModal({ type: 'icon', accept: '.png', iconSize: 256 })}
              >
                Upload .png
              </Button>
            </div>
          </div>
        </div>
        <div className="w-100 mt-3">
          <Form.Label className="mb-1">Settings</Form.Label>
          <div className="d-flex flex-column gap-2 border p-3 overflow-auto">
            {settingsArray.map(([name, key]) => (
              <div className="d-flex" key={key}>
                <p className="bo-text-md p-0 m-0 bo-min-w-400">{name}</p>
                <p className="bo-text-md p-0 m-0 bo-min-w-280">{reward.data[key]}</p>
                <div className="bo-min-w-100">
                  <Button
                    className="bo-common-inline-button text-nowrap"
                    onClick={() =>
                      setSettingsModal({
                        name,
                        key,
                        type: reward.category === 'CHALLENGE' ? 'REAL' : 'INTEGER',
                      })
                    }
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
            {(reward.category === 'VIDEO' || reward.category === 'ARTICLE') && (
              <>
                <div className="d-flex">
                  <p className="bo-text-md p-0 m-0 bo-min-w-400">English File</p>
                  <p className="bo-text-md p-0 m-0 bo-min-w-280">{reward.data.fileName.en}</p>
                  <div className="d-flex gap-3 bo-min-w-200">
                    <Button
                      className="bo-common-inline-button text-nowrap"
                      onClick={() =>
                        setUploadFileModal({
                          type: 'file',
                          accept: reward.category === 'VIDEO' ? '.mp4' : '.html',
                          language: 'en',
                        })
                      }
                    >
                      Upload
                    </Button>
                    <Button
                      className="bo-common-inline-button text-nowrap"
                      disabled={!reward.data.fileName.en}
                      onClick={() => {
                        const fileName = reward.data.fileName.en;
                        const newWindow = window.open(
                          `https://bridgeover.app/resources/rewards/${reward.type}/${fileName}`,
                          '_blank',
                          'noopener,noreferrer',
                        );

                        if (newWindow) newWindow.opener = null;
                      }}
                    >
                      Open
                    </Button>
                  </div>
                </div>
                <div className="d-flex">
                  <p className="bo-text-md p-0 m-0 bo-min-w-400">Spanish File</p>
                  <p className="bo-text-md p-0 m-0 bo-min-w-280">{reward.data.fileName.es}</p>
                  <div className="d-flex gap-3 bo-min-w-200">
                    <Button
                      className="bo-common-inline-button text-nowrap"
                      onClick={() =>
                        setUploadFileModal({
                          type: 'file',
                          accept: reward.category === 'VIDEO' ? '.mp4' : '.html',
                          language: 'es',
                        })
                      }
                    >
                      Upload
                    </Button>
                    <Button
                      className="bo-common-inline-button text-nowrap"
                      disabled={!reward.data.fileName.es}
                      onClick={() => {
                        const fileName = reward.data.fileName.es;
                        const newWindow = window.open(
                          `https://bridgeover.app/resources/rewards/${reward.type}/${fileName}`,
                          '_blank',
                          'noopener,noreferrer',
                        );

                        if (newWindow) newWindow.opener = null;
                      }}
                    >
                      Open
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="w-100 my-3">
          <Form.Label className="mb-1">Assign To</Form.Label>
          <div className="border p-3">
            <div className="d-flex flex-column gap-2">
              {reward?.assignTo?.aggregators?.length > 0 ? (
                reward.assignTo.aggregators.map(x => (
                  <div key={x} className="d-flex gap-2 align-items-center">
                    <Form.Control
                      className="bo-input bo-w-400"
                      readOnly
                      value={aggregators.find(agg => agg.id === x)?.name}
                    />
                    <Button
                      className="bo-common-base-button"
                      onClick={() =>
                        setReward(prev => ({
                          ...prev,
                          assignTo: {
                            ...prev.assignTo,
                            aggregators: prev.assignTo.aggregators.filter(y => y !== x),
                          },
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faTrashAlt} color="#5946A4" size="1x" />
                    </Button>
                  </div>
                ))
              ) : (
                <Form.Label className="mb-1">All Aggregators</Form.Label>
              )}
            </div>
            <Button className="bo-common-inline-button mt-2" onClick={() => setAssignToModal(true)}>
              {reward?.assignTo?.aggregators?.length > 0
                ? 'Add Aggregator'
                : 'Select Specific Aggregators'}
            </Button>
          </div>
        </div>
        {(reward.category === 'VIDEO' ||
          reward.category === 'ARTICLE' ||
          reward.category === 'SURVEY') && (
          <div className="w-100 mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <Form.Label className="mb-1">
                {reward.category === 'SURVEY' ? 'Survey' : 'Knowledge Check'}
              </Form.Label>
              <div className="d-flex gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  hidden
                  onChange={handleFileUpload}
                />
                <Button className="bo-common-inline-button text-nowrap" onClick={importKNC}>
                  Import
                </Button>
                <Button className="bo-common-inline-button text-nowrap" onClick={exportKNC}>
                  Export
                </Button>
              </div>
            </div>
            <ReactJsonEditor
              value={reward.category === 'SURVEY' ? reward.survey : reward.knowledgeCheck}
              getJsonEditor={editor => {
                jsonEditorRef.current = editor || null;
              }}
            />
          </div>
        )}
      </div>
      <RewardUploadFileModal
        uploadFileModal={uploadFileModal}
        closeModal={() => setUploadFileModal(null)}
        rewardType={reward.type}
        setReward={setReward}
        setUpdateIcon={setUpdateIcon}
      />
      <RewardSettingsModal
        settingsModal={settingsModal}
        closeModal={() => setSettingsModal(null)}
        rewardData={reward.data}
        setReward={setReward}
      />
      <RewardAssignToModal
        assignToModal={assignToModal}
        closeModal={() => setAssignToModal(false)}
        aggregators={aggregators}
        rewardAssignTo={reward.assignTo}
        setReward={setReward}
      />
    </>
  );
}

export default Reward;
